import { useState } from 'react';
import { Plane, BedDouble, Lightbulb, ArrowRight } from 'lucide-react';
import { btnPrimary, btnOutline, SectionLabel, money, fmtDate } from './ui';
import TripPlanner from './TripPlanner';

export default function TravelResults({ results, meta, search, searched, isAuthenticated, onSignUp }) {
  const [selected, setSelected] = useState(null);
  const budget = search.budget ? parseFloat(search.budget) : null;

  return (
    <div>
      <SectionLabel>
        {searched && meta
          ? `Trip finder · ${search.month || 'any time'} · ${meta.days} days · from ${meta.origin}`
          : 'Trip finder'}
      </SectionLabel>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
            {searched ? (budget ? `Where ${money(budget)} goes furthest` : 'Best value right now') : 'Popular destinations'}
          </h2>
          <p className="text-slate-500 mt-1">
            {searched
              ? `Ranked with today's FonderX rates. ${meta?.live_prices ? 'Flight prices are live.' : 'Flight figures are typical fares until you tap Flights.'}`
              : 'Set a budget and month above to rank these for your trip.'}
          </p>
        </div>
        {searched && meta && <p className="text-sm text-slate-500">{fmtDate(meta.depart_date)} – {fmtDate(meta.return_date)} · {meta.travelers} {meta.travelers === 1 ? 'traveller' : 'travellers'}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {results.map((c, i) => {
          const b = c.budget || {};
          const est = b.trip_estimate_cad;
          const fill = budget && est ? Math.min(100, Math.round((est / budget) * 100)) : null;
          return (
            <div key={c.id || i} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_28px_rgba(0,0,0,0.10)] transition-shadow">
              <div className="relative h-40">
                <img src={c.photo} alt={c.name} loading="lazy" className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=800'; }} />
                {searched && i < 3 && <span className="absolute top-3 left-3 bg-white text-slate-900 text-xs font-bold px-2.5 py-1 rounded-lg shadow">#{i + 1} pick</span>}
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-0.5">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><span className="text-xl">{c.flag}</span>{c.name}</h3>
                  {c.ai_score && <span className="bg-[#001435] text-white text-sm font-bold px-2.5 py-1 rounded-lg">{c.ai_score}</span>}
                </div>
                <p className="text-sm text-slate-500 mb-3">{c.gateway_city ? `via ${c.gateway_city}` : c.cities?.[0]}{c.depart_date && ` · ${fmtDate(c.depart_date)} – ${fmtDate(c.return_date)}`}</p>

                <div className="divide-y divide-slate-100 text-sm">
                  {c.flight_price_cad && <div className="flex justify-between py-2"><span className="text-slate-600">Flights {c.flight_price_source === 'live' ? '' : '(est.)'}</span><span className="font-mono font-semibold text-slate-900">{money(c.flight_price_cad)}</span></div>}
                  <div className="flex justify-between py-2"><span className="text-slate-600">Per day</span><span className="font-mono font-semibold text-slate-900">{c.dailyCost?.replace(' CAD', '') || '—'}</span></div>
                  {c.fonderx_rate && <div className="flex justify-between py-2"><span className="text-slate-600">1 {c.currency} at FonderX</span><span className="font-mono font-semibold text-slate-900">{c.fonderx_rate.we_sell}</span></div>}
                </div>

                {est && (
                  <div className="mt-3">
                    {fill != null && <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className={`h-full ${b.within_budget ? 'bg-green-500' : 'bg-red-500'}`} style={{ width: `${fill}%` }} /></div>}
                    <div className="flex justify-between text-xs mt-1.5">
                      <span className="text-slate-500">{money(est)}{budget ? ` of ${money(budget)}` : ` for ${meta?.days || search.days} days`}</span>
                      {budget && <span className={`font-semibold ${b.within_budget ? 'text-green-600' : 'text-red-600'}`}>{b.within_budget ? `${money(b.budget_left_cad)} to spare` : `${money(-b.budget_left_cad)} over`}</span>}
                    </div>
                  </div>
                )}

                <p className="text-sm text-slate-600 mt-3 line-clamp-2">{c.ai_summary || c.overview}</p>
                {c.ai_tip && <p className="text-xs text-slate-500 mt-2 flex gap-1.5"><Lightbulb className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />{c.ai_tip}</p>}

                <button onClick={() => setSelected(c)} className={`${btnPrimary} w-full mt-4 h-11`}>Plan this trip <ArrowRight className="w-4 h-4" /></button>
                {c.links && (
                  <div className="flex gap-2 mt-2">
                    <a href={c.links.flights_google} target="_blank" rel="noopener noreferrer" className={`${btnOutline} flex-1`}><Plane className="w-4 h-4" /> Flights</a>
                    <a href={c.links.hotels_booking} target="_blank" rel="noopener noreferrer" className={`${btnOutline} flex-1`}><BedDouble className="w-4 h-4" /> Hotels</a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!isAuthenticated && (
        <div className="mt-10 bg-[#001435] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <h3 className="text-2xl font-bold text-white mb-1">Lock in a better rate before you fly</h3>
            <p className="text-blue-100/60">Verified FonderX customers get preferred rates on every currency above.</p>
          </div>
          <button onClick={onSignUp} className={btnPrimary}>Create free account <ArrowRight className="w-4 h-4" /></button>
        </div>
      )}

      {selected && (
        <TripPlanner country={selected} search={{ ...search, days: meta?.days || search.days, travelers: meta?.travelers || search.travelers }}
          onClose={() => setSelected(null)} isAuthenticated={isAuthenticated} onSignUp={onSignUp} />
      )}
    </div>
  );
}
